/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { IconButton } from "@atlaskit/button-next/new";
import CrossIcon from "@atlaskit/icon/core/cross";
import { JiraIcon } from "@atlaskit/logo-next";
import { Flex } from "@atlaskit/primitives-next/compiled";

import { wacToken } from "@/libraries/design/foundations";
import { messages } from "@/templates/paid-landing/sections/cfind-4666-frictionless-first-look/messages.ts";
import { useJiraAiFFL } from "@/templates/paid-landing/src/hooks/use-jira-ai-ffl-context";

import { PaidJiraAiTeamTypeMadLibPrompt } from "../paid-jira-ai-team-type-mad-lib-prompt";

import { Background } from "./background";
import { Header } from "./header";
import { PreviewTable } from "./preview-table";

import type { TableRowProps } from "./preview-table/table-row";

export interface PaidJiraAiPreviewModalProps {
    onClose?: () => void;
}

const REVEAL_INTERVAL_MS = 250;
type PreviewRow = Omit<TableRowProps, "isLastRow">;

const backgroundCss = css({
    position: "fixed",
    zIndex: 100,
    width: "100vw",
    height: "100vh",
    // eslint-disable-next-line @atlaskit/design-system/no-physical-properties
    left: 0,
    // eslint-disable-next-line @atlaskit/design-system/no-physical-properties
    top: 0,
    background: wacToken("color.neutral.light0")
});

const mainContainerCss = css({
    position: "fixed",
    insetInlineStart: 0,
    insetBlockStart: 0,
    width: "100vw",
    height: "100vh",
    zIndex: 101,
    padding: "20px 28px",
    overflow: "scroll"
});

const headerContainerCss = css({
    willChange: "transform, opacity",
    position: "absolute",
    width: "100%",
    transform: "translateY(-200%)",
    opacity: 0,
    transition:
        "transform 0.4s cubic-bezier(0.4,0,0.2,1), opacity 0.4s cubic-bezier(0.4,0,0.2,1)"
});
const headerContainerShowCss = css({
    transform: "translateY(0)",
    opacity: 1
});

const bodyContainerCss = css({
    willChange: "transform",
    position: "relative",
    transition: "transform 0.4s cubic-bezier(0.4,0,0.2,1)"
});

const promptContainerCss = css({
    willChange: "transform, opacity",
    position: "relative",
    transition:
        "transform 0.4s cubic-bezier(0.4,0,0.2,1), opacity 0.4s cubic-bezier(0.4,0,0.2,1)"
});
const promptContainerHideCss = css({
    transform: "translateY(100%)",
    opacity: 0
});

export const PaidJiraAiPreviewModal = ({
    onClose
}: PaidJiraAiPreviewModalProps) => {
    const intl = useIntl();
    const {
        cgsDataLoading,
        formattedCgsData,
        rawCgsData,
        firePaidJiraAi,
        promptAttemptIndex,
        isErrorFromCgs
    } = useJiraAiFFL();
    const [showJiraSpaceReady, setShowJiraSpaceReady] = useState(false);
    const [progressiveRows, setProgressiveRows] = useState<
        Array<PreviewRow | undefined> | undefined
    >(undefined);
    const revealTimeoutsRef = useRef<number[]>([]);
    const modalOpenedAtRef = useRef<number | null>(null);
    const revealStartedAtRef = useRef<number | null>(null);
    const rowsRevealedCountRef = useRef(0);
    const revealPlannedMsRef = useRef<number | null>(null);
    const firePaidJiraAiRef = useRef(firePaidJiraAi);
    const promptAttemptIndexRef = useRef(promptAttemptIndex);
    const headerContainerRef = useRef<HTMLDivElement>(null);
    const [showLoadingState, setShowLoadingState] = useState<boolean>(false);
    const [bodyContainerPositionY, setBodyContainerPositionY] =
        useState<number>(0);

    const clearRevealTimeouts = useCallback(() => {
        revealTimeoutsRef.current.forEach((t) => window.clearTimeout(t));
        revealTimeoutsRef.current = [];
    }, []);

    // Disable scrolling in body
    useEffect(() => {
        window.document.body.style.overflow = "hidden";

        return () => {
            window.document.body.style.overflow = "";
        };
    }, []);

    useEffect(() => {
        firePaidJiraAiRef.current = firePaidJiraAi;
    }, [firePaidJiraAi]);

    useEffect(() => {
        promptAttemptIndexRef.current = promptAttemptIndex;
    }, [promptAttemptIndex]);

    useEffect(() => {
        modalOpenedAtRef.current = performance.now();
        firePaidJiraAiRef.current("PreviewModalOpened", "viewed", {
            promptAttemptIndex: promptAttemptIndexRef.current
        });

        // Cleanup on unmount.
        return () => clearRevealTimeouts();
    }, []);

    // Show loading UI while loading
    useEffect(() => {
        if (cgsDataLoading) {
            clearRevealTimeouts();
            setShowJiraSpaceReady(false);
            setProgressiveRows(undefined);
        }
    }, [cgsDataLoading]);

    useEffect(() => {
        if (cgsDataLoading) {
            setShowLoadingState(true);
        }
    }, [cgsDataLoading]);

    // Progressive reveal: append rows one-by-one so customers see "AI generating" behavior.
    // Note: this is a UI effect; the API response still arrives as a single payload.
    useEffect(() => {
        if (!formattedCgsData || formattedCgsData.length === 0) {
            return;
        }

        clearRevealTimeouts();
        setShowJiraSpaceReady(false);
        rowsRevealedCountRef.current = 0;
        revealStartedAtRef.current = performance.now();
        revealPlannedMsRef.current =
            formattedCgsData.length * REVEAL_INTERVAL_MS;

        firePaidJiraAiRef.current("PreviewRevealStarted", "started", {
            promptAttemptIndex: promptAttemptIndexRef.current,
            rowCount: formattedCgsData.length,
            revealPlannedMs: revealPlannedMsRef.current
        });

        // Keep a fixed height while revealing (shows placeholder rows).
        const minRowCount = 6;
        const targetLen = Math.max(minRowCount, formattedCgsData.length);
        setProgressiveRows(Array.from({ length: targetLen }));

        formattedCgsData.forEach((row, idx) => {
            const t = window.setTimeout(() => {
                setProgressiveRows((prev) => {
                    if (!prev) {
                        return prev;
                    }
                    const next = [...prev];
                    next[idx] = row;
                    rowsRevealedCountRef.current = Math.max(
                        rowsRevealedCountRef.current,
                        idx + 1
                    );
                    return next;
                });
            }, idx * REVEAL_INTERVAL_MS);
            revealTimeoutsRef.current.push(t);
        });

        // Mark workspace ready only after the table finishes populating.
        const done = window.setTimeout(() => {
            setProgressiveRows(formattedCgsData);
            setShowJiraSpaceReady(true);
            setShowLoadingState(false);

            const now = performance.now();
            const revealDurationMs = revealStartedAtRef.current
                ? Math.max(0, now - revealStartedAtRef.current)
                : undefined;
            const dwellMs = modalOpenedAtRef.current
                ? Math.max(0, now - modalOpenedAtRef.current)
                : undefined;

            firePaidJiraAiRef.current("PreviewReady", "succeeded", {
                promptAttemptIndex: promptAttemptIndexRef.current,
                rowCount: formattedCgsData.length,
                revealDurationMs,
                revealPlannedMs: revealPlannedMsRef.current ?? undefined,
                dwellMs
            });
        }, formattedCgsData.length * REVEAL_INTERVAL_MS + 50);
        revealTimeoutsRef.current.push(done);
    }, [formattedCgsData]);

    const closeMessage = intl.formatMessage(messages.close);

    const handleClose = useCallback(() => {
        const now = performance.now();
        const dwellMs = modalOpenedAtRef.current
            ? Math.max(0, now - modalOpenedAtRef.current)
            : undefined;

        firePaidJiraAiRef.current("PreviewModalClosed", "closed", {
            promptAttemptIndex: promptAttemptIndexRef.current,
            closeReason: "xButton",
            dwellMs,
            rowsRevealedCount: rowsRevealedCountRef.current
        });
        onClose?.();
    }, [onClose]);

    // Position adjustment for sliding animation
    // Adjust y position for body element when header height changes
    useEffect(() => {
        if (!showJiraSpaceReady) {
            setBodyContainerPositionY(0);
            return;
        }

        const updatePosition = () => {
            const headerHeight = headerContainerRef.current?.offsetHeight || 0;
            setBodyContainerPositionY(headerHeight);
        };

        setTimeout(updatePosition);
        window.addEventListener("resize", updatePosition);

        // Cleanup
        return () => {
            window.removeEventListener("resize", updatePosition);
        };
    }, [isErrorFromCgs, showJiraSpaceReady]); // Keep isErrorFromCgs, ref height changes on error

    return (
        <>
            <div css={backgroundCss}>
                <Background />
            </div>
            <div css={mainContainerCss}>
                <Flex alignItems="center" justifyContent="space-between">
                    <JiraIcon appearance="brand" shouldUseNewLogoDesign />
                    <IconButton
                        shape="circle"
                        icon={CrossIcon}
                        label={closeMessage}
                        appearance="default"
                        onClick={handleClose}
                    />
                </Flex>
                <div
                    css={[
                        headerContainerCss,
                        showJiraSpaceReady && headerContainerShowCss
                    ]}
                    ref={headerContainerRef}
                >
                    <Header
                        onRetryClick={() => {
                            firePaidJiraAi("PreviewRetryClicked", "clicked", {
                                promptAttemptIndex,
                                rowsRevealedCount: rowsRevealedCountRef.current
                            });
                            clearRevealTimeouts();
                            setShowJiraSpaceReady(false);
                            setProgressiveRows(undefined);
                        }}
                    />
                </div>
                <div
                    style={{
                        transform: `translateY(${bodyContainerPositionY}px)`
                    }}
                    css={bodyContainerCss}
                >
                    <PreviewTable
                        rows={progressiveRows ?? formattedCgsData}
                        spaceName={
                            cgsDataLoading
                                ? undefined
                                : rawCgsData?.sampleIssues.spaceName
                        }
                    />
                    <div
                        css={[
                            promptContainerCss,
                            showJiraSpaceReady && promptContainerHideCss
                        ]}
                    >
                        <PaidJiraAiTeamTypeMadLibPrompt
                            isRerun
                            showLoadingState={showLoadingState}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};
